/**
 * Capture transform for HeatMapGrid.
 *
 * The heatmap uses a 2×2 CSS grid:
 *   [spacer]          [column headers – overflowX:auto]
 *   [row headers – overflowY:auto]  [grid area – overflow:auto]
 *
 * Simply setting overflow:visible breaks the layout because the grid cells
 * don't grow. Instead we:
 *   1. Measure scrollWidth / scrollHeight of the scroll containers
 *   2. Resize the outer grid to use concrete px values instead of 1fr
 *   3. Set each scroll container's width/height to its full scroll extent
 *   4. Remove maxHeight on the outer container
 *
 * Returns a restore() function.
 */
export async function prepareHeatMapGrid(container) {
  const grid = container.querySelector(".heatMapGridContainer");
  if (!grid) return () => {};

  const hScroll = grid.querySelector(".heatMapScrollContainerHorizontal");
  const vScroll = grid.querySelector(".heatMapScrollContainerVertical");
  const gridArea = grid.querySelector(".heatMapGridArea");

  const yAxisInner = container.querySelector(".heatMapYAxisLabel");
  const yAxisOuter = container.querySelector(".heatMapYAxisLabelContainer");

  // ── Save originals ──────────────────────────────────────────────────────
  const saved = {
    gridTemplateColumns: grid.style.gridTemplateColumns,
    gridTemplateRows: grid.style.gridTemplateRows,
    maxHeight: grid.style.maxHeight,
    overflow: grid.style.overflow,
    hScroll: {
      width: hScroll?.style.width,
      overflowX: hScroll?.style.overflowX,
      overflowY: hScroll?.style.overflowY,
    },
    vScroll: {
      height: vScroll?.style.height,
      overflowX: vScroll?.style.overflowX,
      overflowY: vScroll?.style.overflowY,
    },
    gridArea: {
      width: gridArea?.style.width,
      height: gridArea?.style.height,
      overflow: gridArea?.style.overflow,
    },
    yAxisInnerDisplay: yAxisInner ? yAxisInner.style.display : null,
  };

  // ── Measure full content sizes ──────────────────────────────────────────
  const contentWidth = gridArea?.scrollWidth ?? 0;
  const contentHeight = gridArea?.scrollHeight ?? 0;

  // Read the first column width (row-header column) from current grid
  const computedCols = window.getComputedStyle(grid).gridTemplateColumns;
  const firstColWidth = computedCols.split(" ")[0]; // e.g. "150px"

  const computedRows = window.getComputedStyle(grid).gridTemplateRows;
  const firstRowHeight = computedRows.split(" ")[0]; // e.g. "120px"

  // ── Apply transforms ────────────────────────────────────────────────────
  // Expand grid template to fit full content
  grid.style.gridTemplateColumns = `${firstColWidth} ${contentWidth}px`;
  grid.style.gridTemplateRows = `${firstRowHeight} ${contentHeight}px`;
  grid.style.maxHeight = "none";
  grid.style.overflow = "visible";

  // Expand column headers to full width
  if (hScroll) {
    hScroll.style.width = contentWidth + "px";
    hScroll.style.overflowX = "visible";
    hScroll.style.overflowY = "visible";
  }

  // Expand row headers to full height
  if (vScroll) {
    vScroll.style.height = contentHeight + "px";
    vScroll.style.overflowX = "visible";
    vScroll.style.overflowY = "visible";
  }

  // Expand main grid area
  if (gridArea) {
    gridArea.style.width = contentWidth + "px";
    gridArea.style.height = contentHeight + "px";
    gridArea.style.overflow = "visible";
  }

  // Fix Y-axis label: html2canvas doesn't support writing-mode or transform:rotate
  // on text, so render the label text onto a canvas and insert as an <img>.
  if (yAxisInner && yAxisOuter) {
    try {
      const text = yAxisInner.textContent;
      const computed = window.getComputedStyle(yAxisInner);
      const fontSize = parseFloat(computed.fontSize);
      const fontFamily = computed.fontFamily;
      const fontWeight = computed.fontWeight;
      const color = computed.color;
      const dpr = window.devicePixelRatio || 1;

      // Measure text width at 1x for layout
      const measureCanvas = document.createElement("canvas");
      const measureCtx = measureCanvas.getContext("2d");
      measureCtx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      const textWidth = measureCtx.measureText(text).width;
      const lineHeight = Math.ceil(fontSize * 1.4);

      // CSS dimensions (what the image will display as)
      const cssWidth = lineHeight;
      const cssHeight = Math.ceil(textWidth) + 4;

      // Create hi-DPI canvas
      const offscreen = document.createElement("canvas");
      offscreen.width = cssWidth * dpr;
      offscreen.height = cssHeight * dpr;
      const ctx = offscreen.getContext("2d");
      ctx.scale(dpr, dpr);

      // Draw rotated text (bottom-to-top, characters 90° CCW)
      ctx.translate(0, cssHeight);
      ctx.rotate(-Math.PI / 2);
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      ctx.fillStyle = color;
      ctx.textBaseline = "middle";
      ctx.fillText(text, 2, lineHeight / 2);

      const img = document.createElement("img");
      img.src = offscreen.toDataURL();
      img.style.width = cssWidth + "px";
      img.style.height = cssHeight + "px";
      img.style.maxHeight = "100%";
      img.className = "__captureYAxisImg";

      // Wait for image to be fully decoded before html2canvas captures
      await img.decode();

      yAxisInner.style.display = "none";
      yAxisOuter.appendChild(img);
    } catch (err) {
      console.warn("[heatMapGrid capture] Y-axis label fix failed:", err);
      // Fallback: hide the broken text entirely
      yAxisInner.style.display = "none";
    }
  }

  // ── Return restore function ─────────────────────────────────────────────
  return () => {
    grid.style.gridTemplateColumns = saved.gridTemplateColumns;
    grid.style.gridTemplateRows = saved.gridTemplateRows;
    grid.style.maxHeight = saved.maxHeight;
    grid.style.overflow = saved.overflow;

    if (hScroll) {
      hScroll.style.width = saved.hScroll.width;
      hScroll.style.overflowX = saved.hScroll.overflowX;
      hScroll.style.overflowY = saved.hScroll.overflowY;
    }
    if (vScroll) {
      vScroll.style.height = saved.vScroll.height;
      vScroll.style.overflowX = saved.vScroll.overflowX;
      vScroll.style.overflowY = saved.vScroll.overflowY;
    }
    if (gridArea) {
      gridArea.style.width = saved.gridArea.width;
      gridArea.style.height = saved.gridArea.height;
      gridArea.style.overflow = saved.gridArea.overflow;
    }
    if (yAxisInner && saved.yAxisInnerDisplay != null) {
      yAxisInner.style.display = saved.yAxisInnerDisplay;
      const tempImg = yAxisOuter?.querySelector(".__captureYAxisImg");
      if (tempImg) tempImg.remove();
    }
  };
}
